import React, { useState, useEffect, useRef, useCallback } from 'react';
import { staffAPI } from '../services/api';
import { authService } from '../services/auth';
import {
  StaffMember,
  StaffFormData,
  StaffCounts,
  StaffPagination,
  APIError,
} from '../types';

type StaffRole = 'faculty' | 'superadmin';
/** '' = no role filter (show everyone) */
type RoleFilter = '' | StaffRole;

interface RoleOption {
  value: StaffRole;
  label: string;
  description: string;
}

const ROLE_OPTIONS: RoleOption[] = [
  {
    value: 'superadmin',
    label: 'Super Admin',
    description: 'Full access including students, analytics and team management.',
  },
  {
    value: 'faculty',
    label: 'Faculty',
    description: 'Can upload and manage documents, and use the AI chat.',
  },
];

const roleLabel = (role: StaffRole) => (role === 'superadmin' ? 'Super Admin' : 'Faculty');

const ROLE_FILTERS: { value: RoleFilter; label: string }[] = [
  { value: '', label: 'All' },
  { value: 'superadmin', label: 'Super Admin' },
  { value: 'faculty', label: 'Faculty' },
];

/* ============================================================
   Add / Edit modal
   ============================================================ */

interface StaffFormModalProps {
  staff: StaffMember | null;
  /** id of the logged-in superadmin — used to block self role changes */
  currentUserId: number | null;
  onClose: () => void;
  onSaved: (message: string, updated?: StaffMember) => void;
}

interface StaffFormState {
  username: string;
  email: string;
  phone_number: string;
  password: string;
  role: StaffRole;
}

const emptyForm: StaffFormState = {
  username: '',
  email: '',
  phone_number: '',
  password: '',
  role: 'faculty',
};

const StaffFormModal: React.FC<StaffFormModalProps> = ({
  staff,
  currentUserId,
  onClose,
  onSaved,
}) => {
  const isEdit = !!staff;
  const isSelf = !!staff && staff.id === currentUserId;
  const [form, setForm] = useState<StaffFormState>(
    staff
      ? {
          username: staff.username,
          email: staff.email,
          phone_number: staff.phone_number || '',
          password: '',
          role: staff.role,
        }
      : emptyForm
  );
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<keyof StaffFormState, string>>>({});
  const [serverError, setServerError] = useState('');
  const [saving, setSaving] = useState(false);

  const handleChange = (field: keyof StaffFormState, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setFieldErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const selectRole = (role: StaffRole) => {
    // The backend refuses self-demotion outright; don't let the form get there
    if (isSelf) return;
    setForm((prev) => ({ ...prev, role }));
  };

  const validate = (): boolean => {
    const errors: Partial<Record<keyof StaffFormState, string>> = {};
    const username = form.username.trim();
    const email = form.email.trim();
    const phone = form.phone_number.trim();

    if (!username) {
      errors.username = 'Username is required';
    } else if (username.length < 3) {
      errors.username = 'Username must be at least 3 characters';
    } else if (username.length > 50) {
      errors.username = 'Username must be at most 50 characters';
    }

    if (!email) {
      errors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      errors.email = 'Please enter a valid email address';
    } else if (email.length > 100) {
      errors.email = 'Email must be at most 100 characters';
    }

    // Phone is optional for staff — only validated once something is typed
    if (phone && !/^\+?\d{10,15}$/.test(phone)) {
      errors.phone_number = 'Phone number must be 10-15 digits';
    }

    if (!isEdit) {
      if (!form.password) {
        errors.password = 'Password is required';
      } else if (form.password.length < 8) {
        errors.password = 'Password must be at least 8 characters';
      }
    } else if (form.password && form.password.length < 8) {
      errors.password = 'Password must be at least 8 characters';
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setServerError('');
    if (!validate()) return;

    setSaving(true);
    try {
      const phone = form.phone_number.trim();
      const payload: Partial<StaffFormData> = {
        username: form.username.trim(),
        email: form.email.trim(),
        role: form.role,
      };
      // On edit an empty string is sent deliberately — the server reads that as
      // "clear the phone number". On add we simply omit it.
      if (phone || isEdit) {
        payload.phone_number = phone;
      }
      if (form.password) {
        payload.password = form.password;
      }

      if (isEdit && staff) {
        const res = await staffAPI.updateStaff(staff.id, payload);
        onSaved('Team member updated successfully', res.user);
      } else {
        await staffAPI.createStaff(payload as StaffFormData);
        onSaved('Team member added successfully');
      }
    } catch (err: any) {
      const apiError = err.response?.data as APIError;
      if (apiError?.errors && apiError.errors.length > 0) {
        setServerError(apiError.errors.map((e2) => e2.msg).join(', '));
      } else {
        setServerError(apiError?.error || `Failed to ${isEdit ? 'update' : 'add'} team member`);
      }
    } finally {
      setSaving(false);
    }
  };

  const inputClass = (hasError?: string) =>
    `input-premium ${hasError ? 'border-bmlc-maroon-400' : ''}`;

  return (
    <div className="modal-overlay">
      {/* Click-away layer */}
      <div className="fixed inset-0" onClick={onClose}></div>

      <div className="modal-panel max-w-md">
        <div className="p-4 sm:p-6">
          <div className="flex items-center justify-between mb-4 sm:mb-5">
            <div className="flex items-center space-x-2.5 min-w-0">
              <div className="w-9 h-9 rounded-lg bg-bmlc-navy-50 ring-1 ring-bmlc-navy-100 flex items-center justify-center flex-shrink-0">
                <svg className="w-4 h-4 sm:w-5 sm:h-5 text-bmlc-navy-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  {isEdit ? (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  ) : (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                  )}
                </svg>
              </div>
              <h2 className="font-serif text-lg sm:text-xl font-bold text-bmlc-navy-900 truncate">
                {isEdit ? 'Edit Team Member' : 'Add Team Member'}
              </h2>
            </div>
            <button onClick={onClose} className="btn-ghost p-1.5 rounded-lg flex-shrink-0" title="Close">
              <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {serverError && (
            <div className="toast-error mb-3 sm:mb-4">
              <span className="text-sm sm:text-base">⚠️</span>
              <p className="text-xs sm:text-sm">{serverError}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-3 sm:space-y-4">
            <div>
              <label className="block text-xs sm:text-sm font-semibold text-bmlc-navy-800 mb-1.5">
                Username <span className="text-bmlc-maroon-500">*</span>
              </label>
              <input
                type="text"
                value={form.username}
                onChange={(e) => handleChange('username', e.target.value)}
                placeholder="Full name"
                className={inputClass(fieldErrors.username)}
              />
              {fieldErrors.username && (
                <p className="text-bmlc-maroon-600 font-medium text-[10px] sm:text-xs mt-1">{fieldErrors.username}</p>
              )}
            </div>

            <div>
              <label className="block text-xs sm:text-sm font-semibold text-bmlc-navy-800 mb-1.5">
                Email <span className="text-bmlc-maroon-500">*</span>
              </label>
              <input
                type="text"
                value={form.email}
                onChange={(e) => handleChange('email', e.target.value)}
                placeholder="name@bmlawcollege.in"
                className={inputClass(fieldErrors.email)}
              />
              {fieldErrors.email && (
                <p className="text-bmlc-maroon-600 font-medium text-[10px] sm:text-xs mt-1">{fieldErrors.email}</p>
              )}
            </div>

            <div>
              <label className="block text-xs sm:text-sm font-semibold text-bmlc-navy-800 mb-1.5">
                Phone Number <span className="text-gray-400 font-normal">(optional)</span>
              </label>
              <input
                type="text"
                value={form.phone_number}
                onChange={(e) => handleChange('phone_number', e.target.value)}
                placeholder="9876543210"
                className={inputClass(fieldErrors.phone_number)}
              />
              {fieldErrors.phone_number && (
                <p className="text-bmlc-maroon-600 font-medium text-[10px] sm:text-xs mt-1">{fieldErrors.phone_number}</p>
              )}
            </div>

            <div>
              <label className="block text-xs sm:text-sm font-semibold text-bmlc-navy-800 mb-1.5">
                Password {!isEdit && <span className="text-bmlc-maroon-500">*</span>}
              </label>
              <input
                type="password"
                value={form.password}
                onChange={(e) => handleChange('password', e.target.value)}
                placeholder={isEdit ? '••••••••' : 'At least 8 characters'}
                className={inputClass(fieldErrors.password)}
              />
              {fieldErrors.password ? (
                <p className="text-bmlc-maroon-600 font-medium text-[10px] sm:text-xs mt-1">{fieldErrors.password}</p>
              ) : (
                <p className="text-gray-500 text-[10px] sm:text-xs mt-1">
                  {isEdit ? 'Leave blank to keep current password' : 'Minimum 8 characters'}
                </p>
              )}
            </div>

            <hr className="divider-gold" />

            <div>
              <label className="block text-xs sm:text-sm font-semibold text-bmlc-navy-800 mb-2">
                Role <span className="text-bmlc-maroon-500">*</span>
              </label>
              <div className="space-y-2">
                {ROLE_OPTIONS.map((option) => {
                  const selected = form.role === option.value;
                  return (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => selectRole(option.value)}
                      disabled={isSelf}
                      aria-pressed={selected}
                      className={`w-full text-left rounded-xl border p-3 transition-all duration-200 ${
                        selected
                          ? 'border-bmlc-gold-400 bg-bmlc-gold-50 shadow-gold-glow'
                          : 'border-bmlc-navy-100 bg-white hover:border-bmlc-navy-200'
                      } ${isSelf ? 'opacity-60 cursor-not-allowed' : ''}`}
                    >
                      <div className="flex items-start gap-2.5">
                        <span
                          className={`mt-0.5 w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                            selected ? 'border-bmlc-gold-500 bg-bmlc-gold-500' : 'border-bmlc-navy-200'
                          }`}
                        >
                          {selected && (
                            <svg className="w-2.5 h-2.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={4} d="M5 13l4 4L19 7" />
                            </svg>
                          )}
                        </span>
                        <span className="min-w-0">
                          <span className="block text-xs sm:text-sm font-semibold text-bmlc-navy-900">
                            {option.label}
                          </span>
                          <span className="block text-[10px] sm:text-xs text-gray-500 mt-0.5 leading-snug">
                            {option.description}
                          </span>
                        </span>
                      </div>
                    </button>
                  );
                })}
              </div>
              {isSelf && (
                <p className="text-gray-500 text-[10px] sm:text-xs mt-1.5">
                  You cannot change your own role — ask another Super Admin.
                </p>
              )}
            </div>

            <div className="flex items-center justify-end space-x-2 sm:space-x-3 pt-2">
              <button
                type="button"
                onClick={onClose}
                className="btn-secondary px-4 py-2 rounded-lg text-xs sm:text-sm font-semibold"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving}
                className={`btn-primary px-6 py-2 rounded-lg text-white font-semibold text-xs sm:text-sm inline-flex items-center space-x-2 ${
                  saving ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              >
                {saving ? (
                  <>
                    <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span>Saving...</span>
                  </>
                ) : (
                  <span>{isEdit ? 'Save Changes' : 'Add Team Member'}</span>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

/* ============================================================
   Staff directory
   ============================================================ */

const StaffManagement: React.FC = () => {
  const [staff, setStaff] = useState<StaffMember[]>([]);
  const [counts, setCounts] = useState<StaffCounts | null>(null);
  const [pagination, setPagination] = useState<StaffPagination | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [search, setSearch] = useState('');
  // Committed search term: fetches key off this, not the input value, so typing
  // doesn't fire a request per keystroke
  const [activeSearch, setActiveSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState<RoleFilter>('');
  const [currentPage, setCurrentPage] = useState(1);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingStaff, setEditingStaff] = useState<StaffMember | null>(null);
  const successTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Read once — the logged-in account can't change while this page is mounted
  const [currentUserId] = useState<number | null>(() => authService.getUser()?.id ?? null);

  // Generation counter: the role-filter pills stay clickable while a request is
  // in flight, so only the newest response may write state
  const requestIdRef = useRef(0);

  const fetchStaff = useCallback(async () => {
    const requestId = ++requestIdRef.current;
    try {
      setLoading(true);
      setError('');
      const response = await staffAPI.getStaff(currentPage, 20, activeSearch, roleFilter);
      if (requestId !== requestIdRef.current) return;
      setStaff(response.staff);
      setCounts(response.counts);
      setPagination(response.pagination);
    } catch (err: any) {
      if (requestId !== requestIdRef.current) return;
      const apiError = err.response?.data as APIError;
      setError(apiError?.error || 'Failed to fetch team members');
    } finally {
      if (requestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  }, [currentPage, activeSearch, roleFilter]);

  // One fetch per committed (page, search, role) combination — never per keystroke
  useEffect(() => {
    fetchStaff();
  }, [fetchStaff]);

  useEffect(() => {
    return () => {
      if (successTimerRef.current) {
        clearTimeout(successTimerRef.current);
      }
    };
  }, []);

  const showSuccess = (message: string) => {
    setSuccess(message);
    if (successTimerRef.current) {
      clearTimeout(successTimerRef.current);
    }
    successTimerRef.current = setTimeout(() => setSuccess(''), 4000);
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    // Both updates batch into one render, so the effect fires a single fetch
    // with page 1 and the new term
    setActiveSearch(search);
    setCurrentPage(1);
  };

  const handleRoleFilter = (value: RoleFilter) => {
    if (value === roleFilter) return;
    setRoleFilter(value);
    setCurrentPage(1);
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  const openAddModal = () => {
    setEditingStaff(null);
    setModalOpen(true);
  };

  const openEditModal = (member: StaffMember) => {
    setEditingStaff(member);
    setModalOpen(true);
  };

  const handleModalSaved = (message: string, updated?: StaffMember) => {
    setModalOpen(false);
    setEditingStaff(null);
    showSuccess(message);
    // Editing your OWN account must refresh the cached user, or the drawer and
    // header keep showing the old username until the next login
    if (updated && currentUserId != null && updated.id === currentUserId) {
      const cached = authService.getUser();
      if (cached) {
        authService.setUser({
          ...cached,
          ...updated,
          // User.phone_number is optional; StaffMember uses null for "unset"
          phone_number: updated.phone_number ?? undefined,
        });
      }
    }
    fetchStaff();
  };

  const handleDelete = async (member: StaffMember) => {
    if (
      !window.confirm(
        `Remove "${member.username}" (${roleLabel(member.role)}) from the team? Their chat history and login records will be deleted, and any documents they uploaded will stay but lose their uploader.`
      )
    ) {
      return;
    }

    try {
      setError('');
      await staffAPI.deleteStaff(member.id);
      showSuccess(`${member.username} was removed from the team`);
      // If that was the last row on this page, step back so the admin isn't
      // stranded on an empty page past the end
      if (staff.length === 1 && currentPage > 1) {
        setCurrentPage(currentPage - 1);
      } else {
        fetchStaff();
      }
    } catch (err: any) {
      // Surface the backend guards verbatim (last superadmin / cannot delete yourself)
      const apiError = err.response?.data as APIError;
      setError(apiError?.error || 'Failed to remove team member');
    }
  };

  const statTiles = [
    {
      label: 'Super Admins',
      value: counts?.superadmin ?? 0,
      icon: (
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
      ),
      iconBg: 'bg-bmlc-navy-50',
      iconColor: 'text-bmlc-navy-600',
    },
    {
      label: 'Faculty',
      value: counts?.faculty ?? 0,
      icon: (
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 14l9-5-9-5-9 5 9 5zm0 0l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14zm-4 6v-7.5l4-2.222" />
      ),
      iconBg: 'bg-bmlc-gold-50',
      iconColor: 'text-bmlc-gold-600',
    },
  ];

  return (
    <div className="px-3 sm:px-4 py-4 sm:py-6 animate-fade-in">
      {/* Header */}
      <div className="mb-5 sm:mb-6">
        <div className="flex items-center gap-3 sm:gap-4">
          <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-bmlc-gold-50 ring-2 ring-bmlc-gold-400 shadow-gold-glow flex items-center justify-center flex-shrink-0">
            <svg className="w-6 h-6 sm:w-7 sm:h-7 text-bmlc-gold-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
          </div>
          <div className="min-w-0">
            <h1 className="font-serif text-2xl sm:text-3xl font-bold text-bmlc-navy-900 leading-tight">
              Staff Management
            </h1>
            <p className="text-xs sm:text-sm text-bmlc-gold-600 font-semibold tracking-wide">
              BM Law College Team Directory
            </p>
            <p className="text-gray-600 text-[11px] sm:text-xs mt-1">
              Add administrators and faculty, and control what each of them can access.
            </p>
          </div>
        </div>
      </div>

      {/* Success Toast */}
      {success && (
        <div className="toast-success mb-3 sm:mb-4">
          <span className="text-sm sm:text-base">✅</span>
          <p className="text-xs sm:text-sm">{success}</p>
        </div>
      )}

      {/* Stat Tiles */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 mb-4 sm:mb-6">
        {statTiles.map((tile) => (
          <div key={tile.label} className="stat-card flex items-center gap-3 sm:gap-4">
            <div className={`w-11 h-11 sm:w-12 sm:h-12 ${tile.iconBg} rounded-full flex items-center justify-center flex-shrink-0`}>
              <svg className={`w-5 h-5 sm:w-6 sm:h-6 ${tile.iconColor}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                {tile.icon}
              </svg>
            </div>
            <div className="min-w-0">
              {loading && !counts ? (
                <div className="skeleton h-8 w-14"></div>
              ) : (
                <p className="stat-value">{tile.value}</p>
              )}
              <p className="stat-label">{tile.label}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Toolbar */}
      <div className="card-premium p-4 sm:p-6 mb-4 sm:mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
          <h2 className="section-title mb-0">Manage Team</h2>
          <button
            onClick={openAddModal}
            className="btn-primary px-4 py-2 rounded-lg text-white font-semibold text-xs sm:text-sm inline-flex items-center justify-center space-x-2 self-start sm:self-auto"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
            <span>Add Team Member</span>
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="card p-4 sm:p-6 mb-4 sm:mb-6">
        <h2 className="section-title">Find Team Members</h2>

        <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-3">
          <div className="flex-1 min-w-0">
            <input
              type="text"
              placeholder="Search by name, email, or phone number..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="input-premium"
            />
          </div>
          <button
            type="submit"
            className="btn-primary px-6 py-2.5 rounded-lg text-white font-semibold text-xs sm:text-sm whitespace-nowrap"
          >
            Search
          </button>
        </form>

        <div className="flex flex-wrap items-center gap-1.5 sm:gap-2 mt-3 sm:mt-4">
          <span className="text-[10px] sm:text-xs font-semibold uppercase tracking-wider text-gray-500 mr-1">
            Role
          </span>
          {ROLE_FILTERS.map((filter) => (
            <button
              key={filter.value || 'all'}
              type="button"
              onClick={() => handleRoleFilter(filter.value)}
              className={`tab-premium ${roleFilter === filter.value ? 'tab-premium-active' : ''}`}
            >
              {filter.label}
            </button>
          ))}
        </div>
      </div>

      {/* Staff List */}
      <div className="card p-4 sm:p-6">
        <div className="flex items-center justify-between gap-3 mb-3 sm:mb-4">
          <h2 className="section-title mb-0">
            Team {pagination && `(${pagination.total_staff})`}
          </h2>
          <button onClick={fetchStaff} className="btn-ghost p-1.5 sm:p-2 rounded-lg flex-shrink-0" title="Refresh">
            <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          </button>
        </div>

        {error && (
          <div className="toast-error mb-3 sm:mb-4">
            <span className="text-sm sm:text-base">⚠️</span>
            <p className="text-xs sm:text-sm">{error}</p>
          </div>
        )}

        {loading ? (
          <div className="py-4 sm:py-6">
            <div className="space-y-3">
              <div className="skeleton h-10 w-full"></div>
              <div className="skeleton h-10 w-full"></div>
              <div className="skeleton h-10 w-full"></div>
              <div className="skeleton h-10 w-2/3"></div>
            </div>
            <p className="text-gray-500 text-xs sm:text-sm mt-3 sm:mt-4 text-center">Loading team...</p>
          </div>
        ) : staff.length === 0 ? (
          <div className="text-center py-10 sm:py-14">
            <div className="w-16 h-16 sm:w-20 sm:h-20 bg-bmlc-navy-50 ring-1 ring-bmlc-navy-100 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4 shadow-soft-sm">
              <svg className="w-8 h-8 sm:w-10 sm:h-10 text-bmlc-navy-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            </div>
            <p className="text-bmlc-navy-900 font-serif font-semibold text-sm sm:text-base mb-1">
              No team members found
            </p>
            <p className="text-gray-400 text-xs sm:text-sm">
              {activeSearch || roleFilter
                ? 'Try adjusting your search or role filter'
                : 'Add an administrator or faculty member to get started'}
            </p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto -mx-4 sm:mx-0 px-4 sm:px-0">
              <table className="table-premium min-w-full">
                <thead>
                  <tr>
                    <th className="text-left">Member</th>
                    <th className="text-left">Role</th>
                    <th className="text-left hidden sm:table-cell">Email</th>
                    <th className="text-left hidden md:table-cell">Phone</th>
                    <th className="text-left hidden lg:table-cell">Added</th>
                    <th className="text-left">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {staff.map((member) => {
                    const isSelf = currentUserId !== null && member.id === currentUserId;
                    return (
                      <tr key={member.id}>
                        <td>
                          <div className="flex items-center space-x-2 sm:space-x-3">
                            <div className="w-8 h-8 sm:w-10 sm:h-10 bg-bmlc-gold-50 ring-2 ring-bmlc-gold-300 rounded-full flex items-center justify-center flex-shrink-0">
                              <span className="text-[10px] sm:text-sm font-serif font-bold text-bmlc-gold-700">
                                {member.username?.charAt(0).toUpperCase() || 'T'}
                              </span>
                            </div>
                            <div className="min-w-0">
                              <div className="flex items-center gap-1.5">
                                <span className="text-xs sm:text-sm font-medium text-bmlc-navy-900 truncate max-w-[100px] sm:max-w-[180px]">
                                  {member.username}
                                </span>
                                {isSelf && (
                                  <span className="badge-navy px-2 py-0 text-[9px] sm:text-[10px] flex-shrink-0">
                                    You
                                  </span>
                                )}
                              </div>
                              <span
                                className="block sm:hidden text-[10px] text-gray-500 truncate max-w-[120px]"
                                title={member.email}
                              >
                                {member.email}
                              </span>
                            </div>
                          </div>
                        </td>
                        <td className="whitespace-nowrap">
                          <span className={member.role === 'superadmin' ? 'badge-navy' : 'badge-gold'}>
                            {roleLabel(member.role)}
                          </span>
                        </td>
                        <td className="whitespace-nowrap text-xs sm:text-sm text-gray-600 hidden sm:table-cell">
                          <div className="truncate max-w-[150px] sm:max-w-[220px]" title={member.email}>
                            {member.email}
                          </div>
                        </td>
                        <td className="whitespace-nowrap text-xs sm:text-sm text-gray-600 hidden md:table-cell">
                          {member.phone_number || <span className="text-gray-400">—</span>}
                        </td>
                        <td className="whitespace-nowrap text-xs sm:text-sm text-gray-600 hidden lg:table-cell">
                          {formatDate(member.created_at)}
                        </td>
                        <td className="whitespace-nowrap text-xs sm:text-sm">
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => openEditModal(member)}
                              className="btn-ghost inline-flex items-center space-x-1 px-2.5 sm:px-3 py-1.5 rounded-lg text-[10px] sm:text-xs font-semibold"
                            >
                              <svg className="w-3 h-3 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                              </svg>
                              <span className="hidden sm:inline">Edit</span>
                            </button>
                            <button
                              onClick={() => handleDelete(member)}
                              disabled={isSelf}
                              title={isSelf ? 'You cannot delete your own account' : 'Delete'}
                              className={`btn-danger-soft inline-flex items-center space-x-1 px-2.5 sm:px-3 py-1.5 rounded-lg text-[10px] sm:text-xs font-semibold ${
                                isSelf ? 'opacity-40 cursor-not-allowed' : ''
                              }`}
                            >
                              <svg className="w-3 h-3 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                              <span className="hidden sm:inline">Delete</span>
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination && pagination.total_pages > 1 && (
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mt-4 sm:mt-6 px-1">
                <div className="text-xs sm:text-sm text-gray-600">
                  Showing {(pagination.current_page - 1) * pagination.per_page + 1} to{' '}
                  {Math.min(pagination.current_page * pagination.per_page, pagination.total_staff)} of{' '}
                  {pagination.total_staff} team members
                </div>
                <div className="flex items-center gap-1.5 sm:gap-2">
                  <button
                    onClick={() => setCurrentPage(pagination.current_page - 1)}
                    disabled={!pagination.has_prev}
                    className={`px-3 sm:px-4 py-1.5 text-xs sm:text-sm font-semibold rounded-full transition-colors ${
                      pagination.has_prev ? 'btn-secondary' : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    }`}
                  >
                    Previous
                  </button>
                  <span className="badge-navy px-3">
                    Page {pagination.current_page} of {pagination.total_pages}
                  </span>
                  <button
                    onClick={() => setCurrentPage(pagination.current_page + 1)}
                    disabled={!pagination.has_next}
                    className={`px-3 sm:px-4 py-1.5 text-xs sm:text-sm font-semibold rounded-full transition-colors ${
                      pagination.has_next ? 'btn-secondary' : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    }`}
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Add/Edit Modal */}
      {modalOpen && (
        <StaffFormModal
          staff={editingStaff}
          currentUserId={currentUserId}
          onClose={() => {
            setModalOpen(false);
            setEditingStaff(null);
          }}
          onSaved={handleModalSaved}
        />
      )}
    </div>
  );
};

export default StaffManagement;
